import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { motion, AnimatePresence } from 'framer-motion';
import styled, { keyframes } from 'styled-components';
import { FaArrowDown, FaArrowUp, FaBolt, FaChartLine, FaExternalLinkAlt, FaGlobe, FaSyncAlt, FaFire } from 'react-icons/fa';

// ─── Constants ────────────────────────────────────────────────────────────────

const GROQ_API_URL = 'https://api.groq.com/openai/v1/chat/completions';
const GROQ_API_KEY = process.env.REACT_APP_GROQ_API_KEY;

const MACRO_SERIES = [
  { id: 'FEDFUNDS', label: 'Fed Funds Rate', units: '%' },
  { id: 'UNRATE', label: 'US Unemployment', units: '%' },
  { id: 'CPIAUCSL', label: 'US CPI Index', units: 'index' },
  { id: 'DGS10', label: 'US 10Y Yield', units: '%' },
];

const SAMPLE_NEWS = [
  {
    title: 'Bitcoin holds key support as traders monitor macro pressure',
    summary: 'Bitcoin is holding above a key technical support zone as market participants keep a close eye on macro developments including Fed commentary and US dollar strength. Traders are watching the daily close carefully before positioning.',
    source: 'Trend Genie',
    url: '',
    publishedUtc: new Date().toISOString(),
    impact: 'High',
    sentiment: 'Neutral',
    coins: ['BTC'],
  },
  {
    title: 'Ethereum network activity surges ahead of upcoming protocol upgrade',
    summary: 'On-chain data shows Ethereum transaction volume and active addresses climbing as anticipation builds around the next major protocol upgrade. Gas fees remain elevated, reflecting strong demand for block space.',
    source: 'Trend Genie',
    url: '',
    publishedUtc: new Date(Date.now() - 3600000).toISOString(),
    impact: 'Medium',
    sentiment: 'Bullish',
    coins: ['ETH'],
  },
  {
    title: 'Solana DeFi TVL reaches multi-month high as ecosystem expands',
    summary: 'Total value locked across Solana-based DeFi protocols has climbed to a multi-month high, with several new protocols launching and attracting significant liquidity. SOL price action continues to reflect growing ecosystem momentum.',
    source: 'Trend Genie',
    url: '',
    publishedUtc: new Date(Date.now() - 7200000).toISOString(),
    impact: 'Medium',
    sentiment: 'Bullish',
    coins: ['SOL'],
  },
  {
    title: 'Fed minutes signal cautious approach to rate adjustments',
    summary: 'The latest Federal Reserve meeting minutes reveal policymakers remain cautious about adjusting interest rates, citing persistent uncertainty in inflation data. Risk assets including crypto responded with mixed price action following the release.',
    source: 'Trend Genie',
    url: '',
    publishedUtc: new Date(Date.now() - 10800000).toISOString(),
    impact: 'High',
    sentiment: 'Bearish',
    coins: ['BTC', 'ETH'],
  },
  {
    title: 'XRP legal clarity drives renewed institutional interest',
    summary: 'Following continued legal developments, XRP has seen increased interest from institutional desks exploring payment corridor use cases. Volume on major exchanges has picked up, with analysts watching the $0.60 zone as a key level.',
    source: 'Trend Genie',
    url: '',
    publishedUtc: new Date(Date.now() - 14400000).toISOString(),
    impact: 'Medium',
    sentiment: 'Bullish',
    coins: ['XRP'],
  },
  {
    title: 'Crypto market consolidates after recent rally',
    summary: 'The broader crypto market is entering a consolidation phase following a sharp rally that pushed total market cap to recent highs. Analysts suggest this is a healthy cooldown before any potential continuation.',
    source: 'Trend Genie',
    url: '',
    publishedUtc: new Date(Date.now() - 18000000).toISOString(),
    impact: 'Low',
    sentiment: 'Neutral',
    coins: ['BTC', 'ETH', 'SOL'],
  },
];

// ─── Data helpers (verbatim from original) ────────────────────────────────────

const readString = (value, fallback = '') => {
  const text = value == null ? '' : String(value).trim();
  return text || fallback;
};

const formatDate = (iso) => {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    timeZone: 'UTC',
  }).format(date).replace(',', ' ·');
};

const fullCoinName = (coin) => ({
  BTC: 'BITCOIN', ETH: 'ETHEREUM', SOL: 'SOLANA', BNB: 'BNB', XRP: 'RIPPLE',
  DOGE: 'DOGECOIN', ADA: 'CARDANO', AVAX: 'AVALANCHE', LINK: 'CHAINLINK', TRX: 'TRON',
}[coin] || coin);

const extractCoins = (text) => {
  const value = String(text || '').toUpperCase();
  const coins = ['BTC', 'ETH', 'SOL', 'BNB', 'XRP', 'DOGE', 'ADA', 'AVAX', 'LINK', 'TRX'];
  return coins.filter((coin) => value.includes(coin) || value.includes(fullCoinName(coin)));
};

const classifySentiment = (text) => {
  const value = String(text || '').toLowerCase();
  const bullish = ['surge', 'breakout', 'rally', 'gain', 'bull', 'record', 'strong', 'approval'];
  const bearish = ['drop', 'fall', 'sell-off', 'bear', 'hack', 'liquidation', 'lawsuit', 'weak'];
  if (bullish.some((word) => value.includes(word))) return 'Bullish';
  if (bearish.some((word) => value.includes(word))) return 'Bearish';
  return 'Neutral';
};

const classifyImpact = (text) => {
  const value = String(text || '').toLowerCase();
  const high = ['etf', 'fed', 'sec', 'regulation', 'rate', 'inflation', 'hack', 'liquidation', 'whale'];
  const low = ['community', 'minor', 'commentary'];
  if (high.some((word) => value.includes(word))) return 'High';
  if (low.some((word) => value.includes(word))) return 'Low';
  return 'Medium';
};

const normalizeNewsItem = (item) => ({
  title: readString(item?.title, 'Market Update'),
  summary: readString(item?.summary || item?.body || item?.description),
  source: readString(item?.source),
  url: readString(item?.url),
  publishedUtc: readString(item?.publishedUtc || item?.published_on),
  impact: readString(item?.impact, classifyImpact(`${item?.title || ''} ${item?.summary || ''}`)),
  sentiment: readString(item?.sentiment, classifySentiment(`${item?.title || ''} ${item?.summary || ''}`)),
  coins: Array.isArray(item?.coins) ? item.coins : extractCoins(`${item?.title || ''} ${item?.summary || ''}`),
});

const parseFredCsv = (csvText) => {
  const lines = csvText.trim().split('\n').slice(1);
  return lines
    .map((line) => line.split(','))
    .map(([date, value]) => ({ date, value: Number(value) }))
    .filter((row) => row.date && Number.isFinite(row.value));
};

const fetchMacroSeries = async (series) => {
  const response = await fetch(`https://fred.stlouisfed.org/graph/fredgraph.csv?id=${series.id}`);
  if (!response.ok) throw new Error(`Failed macro fetch for ${series.id}`);
  const text = await response.text();
  const rows = parseFredCsv(text);
  const latest = rows[rows.length - 1];
  const previous = rows[rows.length - 2];
  const trend = latest && previous
    ? (latest.value > previous.value ? 'up' : latest.value < previous.value ? 'down' : 'flat')
    : 'flat';
  return { label: series.label, units: series.units, date: latest?.date || '', value: latest?.value ?? 0, trend };
};

// ── Source 1: CryptoCompare ───────────────────────────────────────────────────
const fetchFromCryptoCompare = async () => {
  const response = await fetch('https://min-api.cryptocompare.com/data/v2/news/?lang=EN');
  if (!response.ok) throw new Error(`CryptoCompare HTTP ${response.status}`);
  const payload = await response.json();
  const list = Array.isArray(payload?.Data) ? payload.Data : [];
  if (!list.length) throw new Error('CryptoCompare returned empty feed');
  return list.slice(0, 15).map((article) => normalizeNewsItem({
    title: article.title,
    summary: article.body,
    source: article.source_info?.name || article.source,
    url: article.url,
    publishedUtc: article.published_on ? new Date(article.published_on * 1000).toISOString() : '',
    coins: article.categories ? extractCoins(article.categories) : [],
  }));
};

// ── Source 2: Professional crypto RSS feeds via rss2json ──────────────────────
// Sources: CoinDesk, CoinTelegraph, Decrypt, The Block, Bitcoin Magazine
const PROFESSIONAL_FEEDS = [
  { rss: 'https://www.coindesk.com/arc/outboundfeeds/rss/',   name: 'CoinDesk'         },
  { rss: 'https://cointelegraph.com/rss',                      name: 'CoinTelegraph'    },
  { rss: 'https://decrypt.co/feed',                            name: 'Decrypt'          },
  { rss: 'https://www.theblock.co/rss.xml',                    name: 'The Block'        },
  { rss: 'https://bitcoinmagazine.com/feed',                   name: 'Bitcoin Magazine' },
];

const RSS2JSON = 'https://api.rss2json.com/v1/api.json';

// Strip HTML tags and decode common entities from RSS description fields
const stripHtml = (raw) =>
  String(raw || '')
    .replace(/<[^>]+>/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&nbsp;/g, ' ')
    .replace(/&#\d+;/g, '')
    .replace(/\s{2,}/g, ' ')
    .trim();

const fetchFromProfessionalRSS = async () => {
  const results = await Promise.allSettled(
    PROFESSIONAL_FEEDS.map(async (feed) => {
      const res = await fetch(
        `${RSS2JSON}?rss_url=${encodeURIComponent(feed.rss)}&count=8`
      );
      if (!res.ok) throw new Error(`RSS fetch failed: ${feed.name}`);
      const json = await res.json();
      if (!Array.isArray(json?.items) || !json.items.length)
        throw new Error(`Empty feed: ${feed.name}`);
      return json.items.map((item) =>
        normalizeNewsItem({
          title:        stripHtml(item.title),
          summary:      stripHtml(item.description || item.content).slice(0, 600),
          source:       feed.name,
          url:          item.link || item.guid || '',
          publishedUtc: item.pubDate ? new Date(item.pubDate).toISOString() : '',
          coins:        extractCoins(
            `${item.title || ''} ${item.description || ''} ${(item.categories || []).join(' ')}`
          ),
        })
      );
    })
  );

  const all = results
    .filter((r) => r.status === 'fulfilled')
    .flatMap((r) => r.value)
    // Keep only stories that are relevant to crypto markets
    .filter((item) => {
      const text = `${item.title} ${item.summary}`.toLowerCase();
      const cryptoTerms = [
        'bitcoin', 'ethereum', 'crypto', 'blockchain', 'defi', 'nft',
        'solana', 'btc', 'eth', 'xrp', 'token', 'altcoin', 'web3',
        'stablecoin', 'wallet', 'exchange', 'mining', 'halving',
        'sec', 'etf', 'fed', 'inflation', 'rate', 'market', 'trading',
      ];
      return cryptoTerms.some((term) => text.includes(term));
    });

  if (!all.length) throw new Error('No relevant crypto stories found in RSS feeds');

  // Sort newest first and deduplicate by title
  const seen = new Set();
  return all
    .sort((a, b) => new Date(b.publishedUtc) - new Date(a.publishedUtc))
    .filter((item) => {
      if (seen.has(item.title)) return false;
      seen.add(item.title);
      return true;
    })
    .slice(0, 18);
};

// ── Composed fetch with automatic fallback ─────────────────────────────────────
const fetchRawNews = async () => {
  try {
    return await fetchFromCryptoCompare();
  } catch (e1) {
    console.warn('[News] CryptoCompare unavailable, switching to professional RSS:', e1.message);
    return await fetchFromProfessionalRSS();
    // If this also throws, loadAll's catch block will show SAMPLE_NEWS
  }
};

const buildFallbackInsights = (items, macro) => {
  const bullishCount = items.filter((item) => String(item.sentiment).toLowerCase().includes('bull')).length;
  const bearishCount = items.filter((item) => String(item.sentiment).toLowerCase().includes('bear')).length;
  const dominantSentiment = bullishCount > bearishCount ? 'Bullish' : bearishCount > bullishCount ? 'Bearish' : 'Balanced';
  const highImpactCount = items.filter((item) => String(item.impact).toLowerCase().includes('high')).length;
  const hotCoins = Array.from(new Set(items.flatMap((item) => item.coins))).slice(0, 5);

  return {
    'Market Regime': `Current headline tone looks ${dominantSentiment.toLowerCase()}, with ${highImpactCount} higher-impact stories in the feed.`,
    'Whale Alerts': hotCoins.length
      ? hotCoins.map((coin) => `${coin} is appearing repeatedly across current headlines.`)
      : ['No strong coin concentration detected right now.'],
    'Funding & Open Interest': 'Use this news page as context. Add a dedicated derivatives feed later if you want exact funding and open interest numbers here.',
    'Economic Calendar': macro.map((point) => `${point.label}: ${point.value} ${point.units}`.trim()),
    'News Sentiment By Coin': hotCoins.length
      ? hotCoins.map((coin) => `${coin}: ${dominantSentiment}`)
      : ['No dominant coin-specific tone detected.'],
    'Trend Reversal Candidates': hotCoins.length
      ? hotCoins.map((coin) => `${coin} deserves chart review if price action starts diverging from headline tone.`)
      : ['Watch majors first before chasing lower-liquidity names.'],
    'Price Zone Alerts': 'Use the headline context here with your Trend Genie signal workflow before acting on breakouts or pullbacks.',
    'Personalized Daily Brief': `Headline tone is ${dominantSentiment.toLowerCase()} right now. Focus on majors first, then confirm alt setups with chart structure.`,
    'Liquidity Heatmap Levels': 'Pair this page with your existing charting tools for exact liquidity and execution levels.',
  };
};

const buildGroqInsights = async (items, macro) => {
  if (!GROQ_API_KEY) return null;

  const prompt = `Return strict JSON for a crypto trader website page. Use only the provided data. Return {"ranked_items":[{"title":"...","summary":"...","impact":"High|Medium|Low","sentiment":"Bullish|Bearish|Neutral","coins":["BTC"]}],"insights":{"Market Regime":"...","Whale Alerts":["..."],"Funding & Open Interest":"...","Economic Calendar":["..."],"News Sentiment By Coin":["..."],"Trend Reversal Candidates":["..."],"Price Zone Alerts":"...","Personalized Daily Brief":"...","Liquidity Heatmap Levels":"..."}}. Macro: ${JSON.stringify(macro)} News: ${JSON.stringify(items)}`;

  const response = await fetch(GROQ_API_URL, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${GROQ_API_KEY}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      model: 'llama-3.1-8b-instant',
      temperature: 0.25,
      response_format: { type: 'json_object' },
      messages: [
        { role: 'system', content: 'You generate concise trader-focused JSON only.' },
        { role: 'user', content: prompt },
      ],
    }),
  });

  if (!response.ok) throw new Error('Groq AI ranking failed.');
  const payload = await response.json();
  return JSON.parse(payload?.choices?.[0]?.message?.content || '{}');
};

// ─── Derivatives / Futures Intelligence fetchers (Binance Futures — free) ─────

const PERP_SYMBOLS = ['BTCUSDT', 'ETHUSDT', 'SOLUSDT', 'BNBUSDT', 'XRPUSDT', 'ADAUSDT'];

// Funding rates + mark price for all major perps in one call
const fetchFundingRates = async () => {
  const res = await fetch('https://fapi.binance.com/fapi/v1/premiumIndex');
  if (!res.ok) throw new Error('Funding rates failed');
  const data = await res.json();
  return data
    .filter(d => PERP_SYMBOLS.includes(d.symbol))
    .map(d => ({
      symbol:      d.symbol.replace('USDT', ''),
      rate:        parseFloat(d.lastFundingRate) * 100,   // already a decimal, *100 = %
      markPrice:   parseFloat(d.markPrice),
      nextFunding: Number(d.nextFundingTime),
    }))
    .sort((a, b) =>
      PERP_SYMBOLS.indexOf(a.symbol + 'USDT') -
      PERP_SYMBOLS.indexOf(b.symbol + 'USDT')
    );
};

// Global long/short account ratio for top 4 perps
const fetchLongShortRatios = async () => {
  const symbols = ['BTCUSDT', 'ETHUSDT', 'SOLUSDT', 'BNBUSDT'];
  const results = await Promise.allSettled(
    symbols.map(async s => {
      const res = await fetch(
        `https://fapi.binance.com/futures/data/globalLongShortAccountRatio?symbol=${s}&period=5m&limit=1`
      );
      if (!res.ok) throw new Error(`LS failed: ${s}`);
      const data = await res.json();
      const row  = data?.[0];
      return {
        symbol:   s.replace('USDT', ''),
        longPct:  Math.round(parseFloat(row?.longAccount  ?? 0.5) * 100),
        shortPct: Math.round(parseFloat(row?.shortAccount ?? 0.5) * 100),
      };
    })
  );
  return results.filter(r => r.status === 'fulfilled').map(r => r.value);
};

// Open interest + 1-hour OI change to spot accumulation or distribution
const fetchOpenInterests = async () => {
  const symbols = ['BTCUSDT', 'ETHUSDT', 'SOLUSDT', 'BNBUSDT'];
  const results = await Promise.allSettled(
    symbols.map(async s => {
      const [cur, hist] = await Promise.allSettled([
        fetch(`https://fapi.binance.com/fapi/v1/openInterest?symbol=${s}`).then(r => r.json()),
        fetch(`https://fapi.binance.com/futures/data/openInterestHist?symbol=${s}&period=1h&limit=2`).then(r => r.json()),
      ]);
      const current = parseFloat(cur.value?.openInterest ?? 0);
      const rows    = hist.status === 'fulfilled' ? hist.value : [];
      const prev    = rows.length >= 2 ? parseFloat(rows[0].sumOpenInterest) : null;
      const change  = prev && prev > 0 ? ((current - prev) / prev) * 100 : null;
      return { symbol: s.replace('USDT', ''), oi: current, change };
    })
  );
  return results.filter(r => r.status === 'fulfilled').map(r => r.value);
};

// ── Helpers ──────────────────────────────────────────────────────────────────

const fundingBias = (rate) => {
  if (rate >  0.05) return { label: 'Very Long Heavy', color: '#dc2626', tip: 'Longs overheated — watch for correction' };
  if (rate >  0.01) return { label: 'Long Bias',       color: '#f97316', tip: 'Longs paying shorts — mild bullish pressure' };
  if (rate > -0.01) return { label: 'Neutral',          color: '#d97706', tip: 'Market balanced — no strong directional bias' };
  if (rate > -0.05) return { label: 'Short Bias',       color: '#22c55e', tip: 'Shorts paying longs — possible short squeeze' };
  return                   { label: 'Very Short Heavy', color: '#10b981', tip: 'Extreme short bias — high squeeze potential' };
};

const formatOI = (val) => {
  if (!val) return '—';
  if (val >= 1e6) return `${(val / 1e6).toFixed(2)}M`;
  if (val >= 1e3) return `${(val / 1e3).toFixed(1)}K`;
  return val.toFixed(2);
};

// ── Fibonacci levels from 30-day Binance klines ───────────────────────────────

const FIBO_COINS = ['BTCUSDT', 'ETHUSDT', 'SOLUSDT', 'BNBUSDT'];

const fetchFiboLevels = async () => {
  const results = await Promise.allSettled(
    FIBO_COINS.map(async (symbol) => {
      const res = await fetch(
        `https://api.binance.com/api/v3/klines?symbol=${symbol}&interval=1d&limit=30`
      );
      if (!res.ok) throw new Error(`Fibo klines failed: ${symbol}`);
      const klines = await res.json();
      // kline format: [openTime, open, high, low, close, ...]
      const high  = Math.max(...klines.map(k => parseFloat(k[2])));
      const low   = Math.min(...klines.map(k => parseFloat(k[3])));
      const price = parseFloat(klines[klines.length - 1][4]); // last close
      const diff  = high - low;
      return {
        symbol: symbol.replace('USDT', ''),
        price,
        high,
        low,
        fib236: high - diff * 0.236,
        fib382: high - diff * 0.382,
        fib50:  high - diff * 0.500,
        fib618: high - diff * 0.618,
        fib786: high - diff * 0.786,
      };
    })
  );
  return results.filter(r => r.status === 'fulfilled').map(r => r.value);
};

const formatFP = (price, symbol) => {
  if (symbol === 'BTC')   return `$${price.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
  if (price >= 10000)     return `$${price.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
  if (price >= 100)       return `$${price.toFixed(1)}`;
  if (price >= 1)         return `$${price.toFixed(2)}`;
  return `$${price.toFixed(4)}`;
};

// ─── UI helpers ───────────────────────────────────────────────────────────────

const POSITIVE = '#059669';
const NEGATIVE = '#dc2626';
const ACCENT = '#d97706';

const timeAgo = (iso) => {
  if (!iso) return '';
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return 'Just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
};

const sentimentMeta = (sentiment) => {
  const v = String(sentiment || '').toLowerCase();
  if (v.includes('bull')) return { label: 'Bullish', color: POSITIVE, icon: '▲', short: 'bull' };
  if (v.includes('bear')) return { label: 'Bearish', color: NEGATIVE, icon: '▼', short: 'bear' };
  return { label: 'Neutral', color: ACCENT, icon: '◆', short: 'neutral' };
};

const impactMeta = (impact) => {
  const v = String(impact || '').toLowerCase();
  if (v.includes('high')) return { label: 'High Impact', color: NEGATIVE, bars: 3 };
  if (v.includes('low')) return { label: 'Low Impact', color: POSITIVE, bars: 1 };
  return { label: 'Medium Impact', color: ACCENT, bars: 2 };
};

const valuePreview = (value) => {
  if (value == null) return 'No data';
  if (typeof value === 'string' || typeof value === 'number') return String(value);
  if (Array.isArray(value)) return value.length ? String(value[0]) : 'No data';
  if (typeof value === 'object') {
    const k = Object.keys(value)[0];
    return k ? `${k}: ${String(value[k])}` : 'No data';
  }
  return 'No data';
};

// ─── Sub-components ───────────────────────────────────────────────────────────

const ImpactBars = ({ bars, color }) => (
  <BarsRow>
    {[1, 2, 3].map(n => (
      <Bar key={n} $active={n <= bars} $color={color} />
    ))}
  </BarsRow>
);

const TickerBand = ({ items }) => {
  const titles = items.map(i => i.title).join('   ·   ');
  return (
    <TickerWrap>
      <TickerLabel><FaBolt /> LIVE</TickerLabel>
      <TickerScroller>
        <TickerInner aria-hidden>
          <span>{titles}</span>
          <span aria-hidden="true">&nbsp;&nbsp;&nbsp;·&nbsp;&nbsp;&nbsp;{titles}</span>
        </TickerInner>
      </TickerScroller>
    </TickerWrap>
  );
};

const FeaturedCard = ({ item }) => {
  const sm = sentimentMeta(item.sentiment);
  const im = impactMeta(item.impact);
  return (
    <FeatCard $color={sm.color}>
      {/* ── Dark header panel ── */}
      <FeatHeader $color={sm.color}>
        <FeatHeaderGlow $color={sm.color} />
        <FeatHeaderTop>
          <FeaturedPill>⚡ Top Story</FeaturedPill>
          <FeatHeaderRight>
            <FeatTimeTag>{timeAgo(item.publishedUtc)}</FeatTimeTag>
            {item.url && (
              <ReadBtn href={item.url} target="_blank" rel="noopener noreferrer">
                Read Article <FaExternalLinkAlt style={{ fontSize: '0.6rem' }} />
              </ReadBtn>
            )}
          </FeatHeaderRight>
        </FeatHeaderTop>
        <FeatBadgeRow>
          <SentPill $color={sm.color}>{sm.icon} {sm.label}</SentPill>
          <ImpPill $color={im.color}>{im.label}</ImpPill>
          {(item.coins.length === 0 ? ['MARKET'] : item.coins.slice(0, 4)).map(c => (
            <CoinBadgeDark key={c}>{c}</CoinBadgeDark>
          ))}
        </FeatBadgeRow>
      </FeatHeader>

      {/* ── White content area ── */}
      <FeatBody>
        <FeatTitle>{item.title}</FeatTitle>
        {item.summary && (
          <FeatSummary>
            {item.summary.slice(0, 380)}{item.summary.length > 380 ? '…' : ''}
          </FeatSummary>
        )}
        <FeatFooter>
          {item.source && <SourceChip>{item.source}</SourceChip>}
          <FeatImpactWrap>
            <FeatImpactLabel>Impact</FeatImpactLabel>
            <ImpactBars bars={im.bars} color={im.color} />
          </FeatImpactWrap>
        </FeatFooter>
      </FeatBody>
    </FeatCard>
  );
};

const SignalCard = ({ item, index }) => {
  const [expanded, setExpanded] = useState(false);
  const sm = sentimentMeta(item.sentiment);
  const im = impactMeta(item.impact);
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.38, delay: index * 0.055, ease: [0.22, 1, 0.36, 1] }}
    >
      <SCard $color={sm.color} $sentiment={sm.short}>
        <STopStrip $color={sm.color} />
        <SBody>
          {/* ── Header row: sentiment + impact + coins + time ── */}
          <STopRow>
            <SBadgeGroup>
              <SentTag $color={sm.color}>{sm.icon} {sm.label}</SentTag>
              <ImpTag $color={im.color}>{im.label}</ImpTag>
              {item.coins.slice(0, 2).map(c => <CoinBadge key={c}>{c}</CoinBadge>)}
            </SBadgeGroup>
            <SMeta>
              <STimeTag>{timeAgo(item.publishedUtc)}</STimeTag>
              {item.url && (
                <ExtBtn href={item.url} target="_blank" rel="noopener noreferrer" title="Open source">
                  <FaExternalLinkAlt />
                </ExtBtn>
              )}
            </SMeta>
          </STopRow>

          {/* ── Title (click to expand) ── */}
          <STitle onClick={() => setExpanded(e => !e)}>{item.title}</STitle>

          {/* ── Expandable summary ── */}
          <AnimatePresence>
            {expanded && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.32, ease: [0.22, 1, 0.36, 1] }}
                style={{ overflow: 'hidden' }}
              >
                <SExpandedBlock $color={sm.color}>
                  <SExpandedLabel $color={sm.color}>Summary</SExpandedLabel>
                  <SSummary>
                    {item.summary && item.summary.length > 30
                      ? item.summary
                      : 'No additional details available for this story at this time.'}
                  </SSummary>
                  {item.url && (
                    <SReadMore href={item.url} target="_blank" rel="noopener noreferrer">
                      Read full article <FaExternalLinkAlt style={{ fontSize: '0.6rem' }} />
                    </SReadMore>
                  )}
                </SExpandedBlock>
              </motion.div>
            )}
          </AnimatePresence>

          {/* ── Footer ── */}
          <SFooter>
            <SourceChip>{item.source || 'Crypto News'}</SourceChip>
            <SFooterRight>
              <ImpactBars bars={im.bars} color={im.color} />
              <ExpandBtn $color={sm.color} onClick={() => setExpanded(e => !e)}>
                {expanded ? '↑ Less' : '↓ Summary'}
              </ExpandBtn>
            </SFooterRight>
          </SFooter>
        </SBody>
      </SCard>
    </motion.div>
  );
};

const MacroWidget = ({ macro }) => {
  if (!macro.length) return null;
  return (
    <Widget>
      <WidgetHeader>
        <WidgetIcon><FaGlobe /></WidgetIcon>
        <WidgetTitle>Macro Dashboard</WidgetTitle>
      </WidgetHeader>
      <MacroList>
        {macro.map(m => (
          <MacroRow key={m.label} $trend={m.trend}>
            <MacroLabel>{m.label}</MacroLabel>
            <MacroDate>{m.date}</MacroDate>
            <MacroRight>
              <MacroVal>{m.value?.toFixed(2)} {m.units}</MacroVal>
              <TrendIcon $trend={m.trend}>
                {m.trend === 'up' ? <FaArrowUp /> : m.trend === 'down' ? <FaArrowDown /> : '—'}
              </TrendIcon>
            </MacroRight>
          </MacroRow>
        ))}
      </MacroList>
    </Widget>
  );
};

const InsightsWidget = ({ insights, loading }) => {
  const entries = Object.entries(insights || {}).slice(0, 5);
  if (loading) return (
    <Widget>
      <WidgetHeader>
        <WidgetIcon><FaChartLine /></WidgetIcon>
        <WidgetTitle>AI Market Intel</WidgetTitle>
      </WidgetHeader>
      {[1, 2, 3].map(i => <SkeletonRow key={i} />)}
    </Widget>
  );
  if (!entries.length) return null;
  return (
    <Widget>
      <WidgetHeader>
        <WidgetIcon $accent><FaChartLine /></WidgetIcon>
        <WidgetTitle>AI Market Intel</WidgetTitle>
      </WidgetHeader>
      <InsightList>
        {entries.map(([key, val]) => (
          <InsightItem key={key}>
            <InsightKey>{key}</InsightKey>
            <InsightVal>{valuePreview(val)}</InsightVal>
          </InsightItem>
        ))}
      </InsightList>
    </Widget>
  );
};

const FIBO_LEVELS = [
  { key: 'fib236', label: '23.6%', color: '#6366f1', desc: 'Shallow pullback — price barely dipped' },
  { key: 'fib382', label: '38.2%', color: '#f59e0b', desc: 'Common bounce zone for strong trends' },
  { key: 'fib50',  label: '50.0%', color: '#29ABE2', desc: 'Mid-range pivot — market undecided' },
  { key: 'fib618', label: '61.8%', color: '#10b981', desc: 'Golden zone — strongest support/resistance' },
  { key: 'fib786', label: '78.6%', color: '#f43f5e', desc: 'Deep retracement — last defence before low' },
];

const TradingIntelWidget = ({ fundingRates, longShort, openInterest, fiboLevels, loading, derivUpdated }) => (
  <Widget>
    <WidgetHeader>
      <WidgetIcon $accent><FaBolt /></WidgetIcon>
      <WidgetTitle>Derivatives Pulse</WidgetTitle>
      <DerivLiveTag>
        <DerivLiveDot />
        {derivUpdated ? `${timeAgo(derivUpdated.toISOString())}` : 'Live'}
      </DerivLiveTag>
    </WidgetHeader>

    {loading ? (
      <IntelLoadWrap>{[1,2,3,4,5].map(i => <SkeletonRow key={i} />)}</IntelLoadWrap>
    ) : (
      <>
        {/* ── Funding Rates ── */}
        {fundingRates.length > 0 && (
          <IntelSection>
            <IntelSectionLabel>Funding Rates <IntelLabelNote>(per 8h)</IntelLabelNote></IntelSectionLabel>
            {fundingRates.map(f => {
              const bias = fundingBias(f.rate);
              return (
                <FRRow key={f.symbol} title={bias.tip}>
                  <FRSymbol>{f.symbol}</FRSymbol>
                  <FRRate $positive={f.rate >= 0}>
                    {f.rate >= 0 ? '+' : ''}{f.rate.toFixed(4)}%
                  </FRRate>
                  <FRBias $color={bias.color}>{bias.label}</FRBias>
                </FRRow>
              );
            })}
          </IntelSection>
        )}

        {/* ── Long / Short Ratio ── */}
        {longShort.length > 0 && (
          <IntelSection>
            <IntelSectionLabel>Long / Short Ratio <IntelLabelNote>(accounts)</IntelLabelNote></IntelSectionLabel>
            {longShort.map(ls => (
              <LSRow key={ls.symbol}>
                <LSSymbol>{ls.symbol}</LSSymbol>
                <LSBarWrap title={`${ls.longPct}% Long · ${ls.shortPct}% Short`}>
                  <LSBarLong style={{ width: `${ls.longPct}%` }} />
                  <LSBarShort style={{ width: `${ls.shortPct}%` }} />
                </LSBarWrap>
                <LSPcts>
                  <LSPct $up>{ls.longPct}%</LSPct>
                  <LSPct>{ls.shortPct}%</LSPct>
                </LSPcts>
              </LSRow>
            ))}
          </IntelSection>
        )}

        {/* ── Open Interest ── */}
        {openInterest.length > 0 && (
          <IntelSection>
            <IntelSectionLabel>Open Interest <IntelLabelNote>(1h change)</IntelLabelNote></IntelSectionLabel>
            <OIGrid>
              {openInterest.map(oi => {
                const up = oi.change !== null && oi.change >= 0;
                return (
                  <OICell key={oi.symbol}>
                    <OISymbol>{oi.symbol}</OISymbol>
                    <OIVal>{formatOI(oi.oi)}</OIVal>
                    {oi.change !== null && (
                      <OIChange $up={up}>{up ? '▲' : '▼'} {Math.abs(oi.change).toFixed(2)}%</OIChange>
                    )}
                  </OICell>
                );
              })}
            </OIGrid>
          </IntelSection>
        )}

        {/* ── Fibonacci Levels ── */}
        {fiboLevels.length > 0 && (
          <IntelSection>
            <IntelSectionLabel>
              Fibonacci Retracement
              <IntelLabelNote>(30D swing)</IntelLabelNote>
            </IntelSectionLabel>

            {/* Educational note */}
            <FiboEducNote>
              <FiboEducIcon>💡</FiboEducIcon>
              <FiboEducText>
                Price zones where markets tend to <strong>pause or reverse</strong>. Levels <em>below price</em> = <strong>support</strong> (bounce up). Levels <em>above price</em> = <strong>resistance</strong> (push down).
              </FiboEducText>
            </FiboEducNote>

            {fiboLevels.map(coin => {
              const allWithPrice = FIBO_LEVELS.map(fl => ({ ...fl, price: coin[fl.key] }));
              const nearest = [...allWithPrice].sort((a, b) =>
                Math.abs(a.price - coin.price) - Math.abs(b.price - coin.price)
              )[0];
              const nearestIsSupport = coin.price >= nearest.price;
              const pricePct = Math.max(2, Math.min(98,
                ((coin.price - coin.low) / (coin.high - coin.low)) * 100
              ));
              // Include swing high/low anchors so there's always at least one support + one resistance
              const allLevels = [
                { key: 'swing_high', label: '0%', color: '#94a3b8', desc: '30D swing high — full range ceiling', price: coin.high },
                ...allWithPrice,
                { key: 'swing_low',  label: '100%', color: '#94a3b8', desc: '30D swing low — full range floor', price: coin.low },
              ];
              const resistLevels = allLevels
                .filter(fl => coin.price < fl.price)
                .sort((a, b) => a.price - b.price) // closest resistance first
                .slice(0, 3);
              const supportLevels = allLevels
                .filter(fl => coin.price >= fl.price)
                .sort((a, b) => b.price - a.price) // closest support first
                .slice(0, 3);

              return (
                <FiboBlock key={coin.symbol}>
                  {/* Coin header row */}
                  <FiboBlockHeader>
                    <FiboBlockSymbol>{coin.symbol}</FiboBlockSymbol>
                    <FiboBlockPrice>{formatFP(coin.price, coin.symbol)}</FiboBlockPrice>
                    <FiboNearestBadge $support={nearestIsSupport}>
                      {nearestIsSupport ? '🛡 Near Support' : '⚔ Near Resistance'}
                    </FiboNearestBadge>
                  </FiboBlockHeader>

                  {/* Visual price-position bar */}
                  <FiboTrackWrap>
                    <FiboTrackEndLabel>L</FiboTrackEndLabel>
                    <FiboTrackBarOuter>
                      <FiboTrackLine />
                      {FIBO_LEVELS.map(({ key, color }) => {
                        const pct = Math.max(2, Math.min(98,
                          ((coin[key] - coin.low) / (coin.high - coin.low)) * 100
                        ));
                        return <FiboLevelTick key={key} $pct={pct} $color={color} />;
                      })}
                      <FiboPriceMarker $pct={pricePct} />
                    </FiboTrackBarOuter>
                    <FiboTrackEndLabel>H</FiboTrackEndLabel>
                  </FiboTrackWrap>
                  <FiboTrackCaption>
                    {formatFP(coin.low, coin.symbol)} → {formatFP(coin.high, coin.symbol)}
                  </FiboTrackCaption>

                  {/* Resistance levels (above current price) */}
                  {resistLevels.length > 0 && (
                    <>
                      <FiboGroupLabel $resist>⚔ Resistance Levels</FiboGroupLabel>
                      {resistLevels.map(({ key, label, color, desc, price }) => (
                        <FiboRow key={key} $highlight={key === nearest.key} $anchor={key === 'swing_high'}>
                          <FiboRatioTag $color={color}>{label}</FiboRatioTag>
                          <FiboLevelInfo>
                            <FiboLevelPrice>{formatFP(price, coin.symbol)}</FiboLevelPrice>
                            <FiboLevelDesc>{desc}</FiboLevelDesc>
                          </FiboLevelInfo>
                          <FiboStatusTag $up={false}>✗ Resist</FiboStatusTag>
                        </FiboRow>
                      ))}
                    </>
                  )}

                  {/* Support levels (below current price) */}
                  {supportLevels.length > 0 && (
                    <>
                      <FiboGroupLabel>🛡 Support Levels</FiboGroupLabel>
                      {supportLevels.map(({ key, label, color, desc, price }) => (
                        <FiboRow key={key} $highlight={key === nearest.key} $anchor={key === 'swing_low'}>
                          <FiboRatioTag $color={color}>{label}</FiboRatioTag>
                          <FiboLevelInfo>
                            <FiboLevelPrice>{formatFP(price, coin.symbol)}</FiboLevelPrice>
                            <FiboLevelDesc>{desc}</FiboLevelDesc>
                          </FiboLevelInfo>
                          <FiboStatusTag $up={true}>✓ Support</FiboStatusTag>
                        </FiboRow>
                      ))}
                    </>
                  )}
                </FiboBlock>
              );
            })}
          </IntelSection>
        )}

        {/* ── CTA ── */}
        <IntelCTA href="https://play.google.com/store/apps/details?id=com.entiresolutions.trendgenie" target="_blank" rel="noopener noreferrer">
          <IntelCTAIcon><FaBolt /></IntelCTAIcon>
          <IntelCTAText>
            <IntelCTATitle>Get Alerts on These Signals</IntelCTATitle>
            <IntelCTADesc>Real-time funding &amp; squeeze alerts in the app</IntelCTADesc>
          </IntelCTAText>
          <IntelCTAArrow>↗</IntelCTAArrow>
        </IntelCTA>
      </>
    )}
  </Widget>
);

const SkeletonCard = () => (
  <SCard $color="#d1d5db" $sentiment="neutral" style={{ minHeight: 120 }}>
    <STopStrip $color="#d1d5db" />
    <SBody>
      <SkeletonLine style={{ width: '60%', height: 12, marginBottom: 10 }} />
      <SkeletonLine style={{ width: '90%', height: 18, marginBottom: 8 }} />
      <SkeletonLine style={{ width: '75%', height: 18, marginBottom: 16 }} />
      <SkeletonLine style={{ width: '40%', height: 10 }} />
    </SBody>
  </SCard>
);

// ─── Main component ───────────────────────────────────────────────────────────

const COIN_FILTERS = ['ALL', 'BTC', 'ETH', 'SOL', 'BNB', 'XRP'];
const SENT_FILTERS = ['ALL', 'Bullish', 'Bearish', 'Neutral'];

const NewsPage = () => {
  const [items,       setItems]       = useState([]);
  const [macro,       setMacro]       = useState([]);
  const [insights,    setInsights]    = useState({});
  const [loading,     setLoading]     = useState(true);
  const [insLoading,  setInsLoading]  = useState(true);
  const [error,       setError]       = useState('');
  const [coinFilter,  setCoinFilter]  = useState('ALL');
  const [sentFilter,  setSentFilter]  = useState('ALL');
  const [lastUpdated, setLastUpdated] = useState(null);
  const [fundingRates,  setFundingRates]  = useState([]);
  const [longShort,     setLongShort]     = useState([]);
  const [openInterest,  setOpenInterest]  = useState([]);
  const [fiboLevels,    setFiboLevels]    = useState([]);
  const [intelLoading,  setIntelLoading]  = useState(true);
  const [derivUpdated,  setDerivUpdated]  = useState(null);

  // ── Derivatives fetch — called independently every 30s ──────────────────────
  const loadDerivatives = async () => {
    const [fr, ls, oi, fb] = await Promise.allSettled([
      fetchFundingRates(),
      fetchLongShortRatios(),
      fetchOpenInterests(),
      fetchFiboLevels(),
    ]);
    if (fr.status === 'fulfilled' && fr.value.length) setFundingRates(fr.value);
    if (ls.status === 'fulfilled' && ls.value.length) setLongShort(ls.value);
    if (oi.status === 'fulfilled' && oi.value.length) setOpenInterest(oi.value);
    if (fb.status === 'fulfilled' && fb.value.length) setFiboLevels(fb.value);
    setDerivUpdated(new Date());
    setIntelLoading(false);
  };

  const loadAll = async () => {
    setLoading(true);
    setInsLoading(true);
    setError('');
    try {
      const [rawNews, macroSnapshot] = await Promise.all([
        fetchRawNews().catch(() => SAMPLE_NEWS),
        Promise.all(MACRO_SERIES.map(fetchMacroSeries)).catch(() => []),
      ]);

      let finalItems = rawNews;
      let finalInsights = buildFallbackInsights(rawNews, macroSnapshot);

      try {
        const groqResult = await buildGroqInsights(rawNews, macroSnapshot);
        if (groqResult?.ranked_items?.length) {
          finalItems = groqResult.ranked_items.map((item, idx) =>
            normalizeNewsItem({
              ...rawNews[idx],
              ...item,
              source: rawNews[idx]?.source,
              url: rawNews[idx]?.url,
              publishedUtc: rawNews[idx]?.publishedUtc,
            })
          );
        }
        if (groqResult?.insights) finalInsights = groqResult.insights;
      } catch { /* keep fallback */ }

      setItems(finalItems);
      setMacro(macroSnapshot);
      setInsights(finalInsights);
      setLastUpdated(new Date());
    } catch (err) {
      setError(err?.message || 'Failed to load the news feed.');
      setItems(SAMPLE_NEWS);
      setMacro([]);
      setInsights(buildFallbackInsights(SAMPLE_NEWS, []));
    } finally {
      setLoading(false);
      setInsLoading(false);
    }
  };

  // Initial load
  useEffect(() => {
    loadAll();
    loadDerivatives();
  }, []);

  // News: refresh every 5 minutes
  useEffect(() => {
    const t = setInterval(loadAll, 5 * 60 * 1000);
    return () => clearInterval(t);
  }, []);

  // Derivatives: refresh every 30 seconds
  useEffect(() => {
    const t = setInterval(loadDerivatives, 30_000);
    return () => clearInterval(t);
  }, []);

  const filtered = useMemo(() => items.filter(item => {
    if (coinFilter !== 'ALL' && !item.coins.includes(coinFilter)) return false;
    if (sentFilter !== 'ALL' && !item.sentiment?.toLowerCase().includes(sentFilter.toLowerCase())) return false;
    return true;
  }), [items, coinFilter, sentFilter]);

  const featured = filtered[0] || null;
  const rest     = filtered.slice(1);

  const schemaData = {
    '@context': 'https://schema.org',
    '@type': 'NewsMediaOrganization',
    name: 'Trend Genie Market News',
    url: 'https://www.trendgenie.io/news',
    description: 'Real-time crypto and finance news with AI-ranked summaries, sentiment analysis, and macro market context.',
  };

  return (
    <>
      <Helmet>
        <title>Crypto News Hub | AI-Ranked Market Intelligence | Trend Genie</title>
        <meta name="description" content="Real-time crypto and financial news ranked by AI. Get sentiment analysis, macro context, and trader-focused market intel from Trend Genie." />
        <meta name="keywords" content="crypto news, bitcoin news today, crypto market news, AI crypto analysis, DeFi news, crypto signals news, market intelligence" />
        <link rel="canonical" href="https://www.trendgenie.io/news" />
        <meta property="og:title" content="Crypto News Hub | AI Market Intelligence | Trend Genie" />
        <meta property="og:description" content="Real-time crypto news ranked by AI — sentiment analysis, macro signals, coin-specific headlines and trader-focused summaries." />
        <meta property="og:image" content="https://www.trendgenie.io/social-share-image.png" />
        <meta property="og:url" content="https://www.trendgenie.io/news" />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Crypto News Hub | Trend Genie" />
        <meta name="twitter:description" content="AI-ranked crypto headlines with sentiment, macro context, and coin-specific intelligence." />
        <meta name="twitter:image" content="https://www.trendgenie.io/social-share-image.png" />
        <meta name="robots" content="index, follow" />
        <script type="application/ld+json">{JSON.stringify(schemaData)}</script>
      </Helmet>

      <PageWrap>

        {/* ── Hero ── */}
        <HeroBand>
          <HeroDecor />
          <HeroOrb />
          <HeroInner>
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
            >
              <Eyebrow>
                <EyebrowDot />
                Live · Crypto &amp; Finance News
              </Eyebrow>
              <HeroH1><span>Market Intelligence</span><br />That Moves With The Market</HeroH1>
              <HeroDesc>
                AI-ranked crypto headlines with real-time sentiment, macro context, and coin-level
                intelligence — built for traders who act fast.
              </HeroDesc>
              <HeroStatRow>
                <StatPill><FaBolt /> {items.length || '—'} stories loaded</StatPill>
                <StatPill>
                  <PulseDot />
                  {lastUpdated ? `Updated ${timeAgo(lastUpdated.toISOString())}` : 'Loading…'}
                </StatPill>
                <RefreshPill type="button" onClick={loadAll} disabled={loading}>
                  <FaSyncAlt style={{ animation: loading ? 'spin 1s linear infinite' : 'none' }} />
                  Refresh
                </RefreshPill>
              </HeroStatRow>
            </motion.div>
          </HeroInner>
        </HeroBand>

        {/* ── Ticker ── */}
        {!loading && items.length > 0 && <TickerBand items={items} />}

        {/* ── Main content ── */}
        <ContentZone>

          {/* Filter bar */}
          <FilterZone>
            <FilterGroup>
              <FilterLabel><FaFire /> Coin</FilterLabel>
              {COIN_FILTERS.map(c => (
                <FilterChip
                  key={c}
                  type="button"
                  $active={coinFilter === c}
                  onClick={() => setCoinFilter(c)}
                >
                  {c}
                </FilterChip>
              ))}
            </FilterGroup>
            <FilterGroup>
              <FilterLabel>Sentiment</FilterLabel>
              {SENT_FILTERS.map(s => (
                <FilterChip
                  key={s}
                  type="button"
                  $active={sentFilter === s}
                  $sentiment={s.toLowerCase()}
                  onClick={() => setSentFilter(s)}
                >
                  {s === 'Bullish' ? '▲ ' : s === 'Bearish' ? '▼ ' : ''}{s}
                </FilterChip>
              ))}
            </FilterGroup>
          </FilterZone>

          {/* Error state */}
          {error && (
            <ErrorBand>
              <ErrorMsg>{error}</ErrorMsg>
              <ErrorBtn type="button" onClick={loadAll}>Try Again</ErrorBtn>
            </ErrorBand>
          )}

          <MainGrid>
            {/* ── Feed column ── */}
            <FeedCol>
              {loading ? (
                <>
                  <SkeletonFeatured />
                  {[1, 2, 3, 4].map(i => <SkeletonCard key={i} />)}
                </>
              ) : filtered.length === 0 ? (
                <EmptyState>No stories match the selected filters.</EmptyState>
              ) : (
                <>
                  {featured && (
                    <motion.div
                      initial={{ opacity: 0, scale: 0.97 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
                    >
                      <FeaturedCard item={featured} />
                    </motion.div>
                  )}
                  <FeedList>
                    {rest.map((item, i) => (
                      <SignalCard key={`${item.title}-${i}`} item={item} index={i} />
                    ))}
                  </FeedList>
                </>
              )}
            </FeedCol>

            {/* ── Sidebar ── */}
            <SideCol>
              <MacroWidget macro={macro} />
              <TradingIntelWidget
                fundingRates={fundingRates}
                longShort={longShort}
                openInterest={openInterest}
                fiboLevels={fiboLevels}
                loading={intelLoading}
                derivUpdated={derivUpdated}
              />
            </SideCol>
          </MainGrid>

        </ContentZone>
      </PageWrap>
    </>
  );
};

export default NewsPage;

// ─── Keyframes ────────────────────────────────────────────────────────────────

const scrollLeft = keyframes`
  0%   { transform: translateX(0); }
  100% { transform: translateX(-50%); }
`;

const floatOrb = keyframes`
  0%, 100% { transform: translateY(-50%) scale(1); opacity: 0.8; }
  50%       { transform: translateY(-58%) scale(1.05); opacity: 1; }
`;

const pulseGlow = keyframes`
  0%, 100% { opacity: 1; transform: scale(1); box-shadow: 0 0 0 0 rgba(16,185,129,0.5); }
  50%       { opacity: 0.75; transform: scale(0.85); box-shadow: 0 0 0 7px rgba(16,185,129,0); }
`;

const spin = keyframes`
  to { transform: rotate(360deg); }
`;

const shimmer = keyframes`
  0%   { background-position: -400px 0; }
  100% { background-position: 400px 0; }
`;

const pulseDot = keyframes`
  0%, 100% { opacity: 1; transform: scale(1); }
  50%       { opacity: 0.5; transform: scale(0.8); }
`;

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to   { opacity: 1; transform: translateY(0);    }
`;

const skeletonBg = keyframes`
  0%   { background-position: -400px 0; }
  100% { background-position: 400px 0; }
`;

// ─── Page structure ───────────────────────────────────────────────────────────

const PageWrap = styled.div`
  background: linear-gradient(180deg, #eef7fd 0%, #f7fbff 30%, #f4f9fe 100%);
  min-height: 100vh;
`;

const HeroBand = styled.section`
  position: relative;
  background: linear-gradient(145deg, #04101d 0%, #071d2e 35%, #0a2d48 65%, #0d3d5c 100%);
  padding: 4rem 8% 3.2rem;
  overflow: hidden;

  @media (max-width: 768px) {
    padding: 2.8rem 5% 2.4rem;
  }
`;

const HeroDecor = styled.div`
  position: absolute;
  inset: 0;
  background-image:
    linear-gradient(rgba(41, 171, 226, 0.06) 1px, transparent 1px),
    linear-gradient(90deg, rgba(41, 171, 226, 0.06) 1px, transparent 1px);
  background-size: 36px 36px;
  pointer-events: none;

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    background:
      radial-gradient(ellipse 60% 40% at 80% 50%, rgba(247,148,29,0.13) 0%, transparent 70%),
      radial-gradient(ellipse 50% 60% at 10% 80%, rgba(41,171,226,0.18) 0%, transparent 70%);
    pointer-events: none;
  }
`;

const HeroOrb = styled.div`
  position: absolute;
  top: 50%;
  right: 8%;
  width: 320px;
  height: 320px;
  border-radius: 50%;
  background: radial-gradient(circle at 40% 40%,
    rgba(41, 171, 226, 0.22) 0%,
    rgba(247, 148, 29, 0.12) 50%,
    transparent 70%
  );
  animation: ${floatOrb} 6s ease-in-out infinite;
  pointer-events: none;
  z-index: 0;
  filter: blur(2px);

  &::after {
    content: '';
    position: absolute;
    inset: 20%;
    border-radius: 50%;
    background: radial-gradient(circle, rgba(41,171,226,0.18) 0%, transparent 70%);
    animation: ${pulseGlow} 3s ease-in-out infinite;
  }

  @media (max-width: 768px) {
    display: none;
  }
`;

const HeroInner = styled.div`
  position: relative;
  z-index: 1;
  max-width: 800px;
`;

const Eyebrow = styled.p`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  margin: 0 0 1rem;
  font-size: 0.72rem;
  font-weight: 800;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: #7fd6ff !important;
  opacity: 1 !important;
`;

const EyebrowDot = styled.span`
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: #10b981;
  animation: ${pulseDot} 1.6s ease-in-out infinite;
`;

const HeroH1 = styled.h1`
  margin: 0 0 1rem;
  font-size: clamp(2rem, 4.5vw, 3.6rem);
  font-weight: 900;
  line-height: 1.1;
  letter-spacing: -0.03em;
  color: #ffffff !important;
  -webkit-text-fill-color: #ffffff !important;
  text-shadow: none !important;

  span {
    background: linear-gradient(135deg, #29ABE2 0%, #7fd6ff 40%, #F7941D 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent !important;
    background-clip: text;
    display: inline;
  }
`;

const HeroDesc = styled.p`
  margin: 0 0 1.4rem;
  font-size: 1.02rem;
  line-height: 1.72;
  color: rgba(210, 232, 244, 0.88) !important;
  max-width: 640px;
  opacity: 1 !important;
`;

const HeroStatRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.6rem;
`;

const StatPill = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.44rem 0.88rem;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.14);
  color: #ffffff;
  font-size: 0.8rem;
  font-weight: 700;
`;

const PulseDot = styled.span`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #10b981;
  animation: ${pulseDot} 1.4s ease-in-out infinite;
`;

const RefreshPill = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.44rem 0.88rem;
  border-radius: 999px;
  background: rgba(41, 171, 226, 0.2);
  border: 1px solid rgba(41, 171, 226, 0.35);
  color: #7fd6ff;
  font-size: 0.8rem;
  font-weight: 700;
  cursor: pointer;
  transition: background 0.2s;

  &:hover:not(:disabled) {
    background: rgba(41, 171, 226, 0.32);
  }

  &:disabled {
    opacity: 0.5;
    cursor: default;
  }

  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;

// ─── Ticker ───────────────────────────────────────────────────────────────────

const TickerWrap = styled.div`
  display: flex;
  align-items: center;
  background: linear-gradient(90deg, #0a1929 0%, #0d2236 100%);
  border-top: 1px solid rgba(41, 171, 226, 0.12);
  border-bottom: 1px solid rgba(41, 171, 226, 0.12);
  height: 40px;
  overflow: hidden;
`;

const TickerLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0 1rem;
  font-size: 0.66rem;
  font-weight: 900;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: #F7941D;
  background: #0d1f2e;
  border-right: 1px solid rgba(41, 171, 226, 0.15);
  height: 100%;
  white-space: nowrap;
  flex-shrink: 0;
`;

const TickerScroller = styled.div`
  flex: 1;
  overflow: hidden;
  height: 100%;
  display: flex;
  align-items: center;
`;

const TickerInner = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 2rem;
  white-space: nowrap;
  animation: ${scrollLeft} 70s linear infinite;
  font-size: 0.79rem;
  color: rgba(200, 220, 235, 0.85);
  font-weight: 600;

  span + span::before {
    content: ' ◆ ';
    color: #F7941D;
    margin-right: 1rem;
    font-size: 0.55rem;
    vertical-align: middle;
    opacity: 0.7;
  }
`;



// ─── Filter zone ──────────────────────────────────────────────────────────────

const ContentZone = styled.div`
  padding: 1.6rem 8% 4rem;

  @media (max-width: 768px) {
    padding: 1.2rem 5% 3rem;
  }
`;

const FilterZone = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem 1.6rem;
  margin-bottom: 1.4rem;
  align-items: center;
  background: #ffffff;
  border-radius: 18px;
  border: 1px solid var(--border-color);
  box-shadow: 0 2px 16px rgba(26, 82, 118, 0.07);
  padding: 0.9rem 1.2rem;
`;

const FilterGroup = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.4rem;
`;

const FilterLabel = styled.span`
  font-size: 0.66rem;
  font-weight: 800;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: var(--text-muted);
  margin-right: 0.2rem;
  display: flex;
  align-items: center;
  gap: 0.3rem;
`;

const FilterChip = styled.button`
  padding: 0.36rem 0.84rem;
  border-radius: 999px;
  font-size: 0.74rem;
  font-weight: 700;
  cursor: pointer;
  border: 1.5px solid;
  transition: all 0.22s cubic-bezier(0.34, 1.56, 0.64, 1);
  letter-spacing: 0.02em;
  transform: scale(1);

  ${({ $active, $sentiment }) => {
    if ($active) {
      if ($sentiment === 'bullish') return `color:#065f46; background:linear-gradient(135deg,rgba(5,150,105,0.18),rgba(5,150,105,0.08)); border-color:rgba(5,150,105,0.45); box-shadow: 0 2px 12px rgba(5,150,105,0.2); transform: scale(1.05);`;
      if ($sentiment === 'bearish') return `color:#991b1b; background:linear-gradient(135deg,rgba(220,38,38,0.15),rgba(220,38,38,0.06)); border-color:rgba(220,38,38,0.38); box-shadow: 0 2px 12px rgba(220,38,38,0.18); transform: scale(1.05);`;
      return `color:#fff; background:linear-gradient(135deg,#29ABE2,#1A5276); border-color:transparent; box-shadow: 0 3px 14px rgba(41,171,226,0.35); transform: scale(1.05);`;
    }
    return `color:var(--text-muted); background:var(--bg-secondary); border-color:var(--border-color);`;
  }}

  &:hover {
    border-color: #29ABE2;
    color: #29ABE2;
    transform: scale(1.04);
  }
`;

// ─── Main grid ────────────────────────────────────────────────────────────────

const MainGrid = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1.6fr) minmax(0, 0.7fr);
  gap: 1.4rem;
  align-items: start;

  @media (max-width: 960px) {
    grid-template-columns: 1fr;
  }
`;

const FeedCol = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.9rem;
`;

const FeedList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.65rem;
`;

const SideCol = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  position: sticky;
  top: 88px;

  @media (max-width: 960px) {
    position: static;
  }
`;

// ─── Featured card ────────────────────────────────────────────────────────────

const FeatCard = styled.article`
  display: flex;
  flex-direction: column;
  background: #ffffff;
  border-radius: 22px;
  border: 1px solid ${({ $color }) => `${$color}30`};
  box-shadow: 0 6px 32px rgba(26, 82, 118, 0.1);
  overflow: hidden;
  transition: box-shadow 0.3s ease, transform 0.3s ease;

  &:hover {
    box-shadow: ${({ $color }) => `0 16px 48px ${$color}2a, 0 4px 16px rgba(26,82,118,0.08)`};
    transform: translateY(-4px);
  }
`;

/* Dark header panel */
const FeatHeader = styled.div`
  position: relative;
  background: linear-gradient(135deg, #071d2e 0%, #0a2d48 60%, #0d3a5c 100%);
  padding: 1.2rem 1.4rem 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  overflow: hidden;
`;

const FeatHeaderGlow = styled.div`
  position: absolute;
  top: -30px;
  right: -30px;
  width: 160px;
  height: 160px;
  border-radius: 50%;
  background: ${({ $color }) => `radial-gradient(circle, ${$color}35 0%, transparent 70%)`};
  pointer-events: none;
`;

const FeatHeaderTop = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 0.5rem;
  position: relative;
  z-index: 1;
`;

const FeatHeaderRight = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const FeatTimeTag = styled.span`
  font-size: 0.66rem;
  font-weight: 600;
  color: rgba(180, 210, 230, 0.75);
  letter-spacing: 0.04em;
`;

const FeatBadgeRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
  align-items: center;
  position: relative;
  z-index: 1;
`;

const CoinBadgeDark = styled.span`
  padding: 0.2rem 0.5rem;
  border-radius: 999px;
  font-size: 0.63rem;
  font-weight: 900;
  letter-spacing: 0.08em;
  color: rgba(200, 230, 245, 0.9);
  background: rgba(255,255,255,0.1);
  border: 1px solid rgba(255,255,255,0.18);
`;

const FeatBody = styled.div`
  flex: 1;
  padding: 1.25rem 1.4rem;
  display: flex;
  flex-direction: column;
  gap: 0.8rem;
`;

const FeatImpactWrap = styled.div`
  display: flex;
  align-items: center;
  gap: 0.4rem;
`;

const FeatImpactLabel = styled.span`
  font-size: 0.62rem;
  font-weight: 700;
  color: #9ca3af;
  letter-spacing: 0.06em;
  text-transform: uppercase;
`;

const FeaturedPill = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  padding: 0.26rem 0.7rem;
  border-radius: 999px;
  font-size: 0.61rem;
  font-weight: 900;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: #fff;
  background: linear-gradient(135deg, #29ABE2 0%, #1A5276 100%);
  box-shadow: 0 2px 10px rgba(41,171,226,0.35);
`;

const SentPill = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 0.26rem 0.6rem;
  border-radius: 999px;
  font-size: 0.66rem;
  font-weight: 800;
  color: ${({ $color }) => $color};
  background: ${({ $color }) => `${$color}14`};
  border: 1px solid ${({ $color }) => `${$color}30`};
`;

const ImpPill = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 0.26rem 0.6rem;
  border-radius: 999px;
  font-size: 0.66rem;
  font-weight: 800;
  color: ${({ $color }) => $color};
  background: ${({ $color }) => `${$color}10`};
  border: 1px solid ${({ $color }) => `${$color}28`};
`;

const ReadBtn = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.38rem 0.82rem;
  border-radius: 10px;
  font-size: 0.72rem;
  font-weight: 800;
  color: #ffffff;
  background: rgba(255,255,255,0.14);
  border: 1px solid rgba(255,255,255,0.25);
  text-decoration: none;
  transition: background 0.18s, transform 0.18s;
  white-space: nowrap;
  letter-spacing: 0.02em;

  &:hover {
    background: rgba(255,255,255,0.24);
    transform: translateY(-1px);
  }
`;

const FeatTitle = styled.h2`
  margin: 0;
  font-size: 1.28rem;
  font-weight: 800;
  line-height: 1.32;
  color: var(--text-color) !important;
  -webkit-text-fill-color: var(--text-color) !important;
  letter-spacing: -0.02em;
`;

const FeatSummary = styled.p`
  margin: 0;
  font-size: 0.91rem;
  line-height: 1.75;
  color: var(--text-muted);
  opacity: 1 !important;
`;

const FeatFooter = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 0.6rem;
  padding-top: 0.6rem;
  border-top: 1px solid var(--border-color);
`;

const CoinRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.35rem;
`;

const CoinBadge = styled.span`
  padding: 0.22rem 0.56rem;
  border-radius: 999px;
  font-size: 0.65rem;
  font-weight: 900;
  letter-spacing: 0.08em;
  color: var(--primary-dark);
  background: linear-gradient(135deg, rgba(41,171,226,0.14) 0%, rgba(41,171,226,0.07) 100%);
  border: 1px solid rgba(41, 171, 226, 0.25);
`;

const FeatMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
`;

const MetaChip = styled.span`
  font-size: 0.72rem;
  font-weight: 700;
  color: var(--text-muted);
`;

const TimeChip = styled.span`
  font-size: 0.68rem;
  font-weight: 600;
  color: #9ca3af;
`;

// ─── Signal card (regular news items) ────────────────────────────────────────

const SCard = styled.article`
  display: flex;
  flex-direction: column;
  background: ${({ $sentiment }) =>
    $sentiment === 'bull'
      ? 'linear-gradient(160deg, rgba(5,150,105,0.04) 0%, #ffffff 40%)'
      : $sentiment === 'bear'
      ? 'linear-gradient(160deg, rgba(220,38,38,0.04) 0%, #ffffff 40%)'
      : 'linear-gradient(160deg, rgba(247,148,29,0.04) 0%, #ffffff 40%)'};
  border-radius: 18px;
  border: 1px solid ${({ $color }) => `${$color}20`};
  box-shadow: 0 2px 12px rgba(26, 82, 118, 0.06);
  overflow: hidden;
  transition: all 0.28s cubic-bezier(0.22, 1, 0.36, 1);

  &:hover {
    box-shadow: ${({ $color }) => `0 10px 36px ${$color}28`};
    transform: translateY(-3px);
    border-color: ${({ $color }) => `${$color}40`};
  }
`;

const STopStrip = styled.div`
  height: 3px;
  background: ${({ $color }) =>
    `linear-gradient(90deg, ${$color} 0%, ${$color}88 60%, transparent 100%)`};
  flex-shrink: 0;
`;

const SBody = styled.div`
  flex: 1;
  padding: 0.9rem 1.1rem;
  display: flex;
  flex-direction: column;
  gap: 0.55rem;
`;

const STopRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.5rem;
`;

const SBadgeGroup = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.3rem;
  align-items: center;
`;

const SentTag = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 0.2rem 0.48rem;
  border-radius: 999px;
  font-size: 0.62rem;
  font-weight: 800;
  color: ${({ $color }) => $color};
  background: ${({ $color }) => `${$color}12`};
  border: 1px solid ${({ $color }) => `${$color}28`};
`;

const ImpTag = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 0.2rem 0.48rem;
  border-radius: 999px;
  font-size: 0.62rem;
  font-weight: 800;
  color: ${({ $color }) => $color};
  background: ${({ $color }) => `${$color}0e`};
  border: 1px solid ${({ $color }) => `${$color}22`};
`;

const STimeTag = styled.span`
  font-size: 0.65rem;
  font-weight: 600;
  color: #9ca3af;
  letter-spacing: 0.02em;
  white-space: nowrap;
`;

const SMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 0.4rem;
  flex-shrink: 0;
`;

const ExtBtn = styled.a`
  width: 30px;
  height: 30px;
  border-radius: 8px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: var(--primary-dark);
  background: var(--primary-light);
  border: 1px solid var(--border-color);
  font-size: 0.68rem;
  text-decoration: none;
  transition: background 0.18s;
  flex-shrink: 0;

  &:hover {
    background: #d0ecf9;
  }
`;

const STitle = styled.h3`
  margin: 0;
  font-size: 0.97rem;
  font-weight: 700;
  line-height: 1.42;
  color: var(--text-color) !important;
  -webkit-text-fill-color: var(--text-color) !important;
  cursor: pointer;
  transition: color 0.18s;

  &:hover {
    color: var(--primary-dark) !important;
    -webkit-text-fill-color: var(--primary-dark) !important;
  }
`;

const SSummary = styled.p`
  margin: 0;
  font-size: 0.86rem;
  line-height: 1.72;
  color: var(--text-muted);
  padding: 0.55rem 0 0.4rem;
  border-top: 1px solid rgba(0,0,0,0.05);
  margin-top: 0.1rem;
  opacity: 1 !important;
`;

const SReadMore = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  font-size: 0.73rem;
  font-weight: 700;
  color: var(--primary-color);
  text-decoration: none;
  padding: 0.3rem 0.7rem;
  border-radius: 8px;
  background: rgba(41,171,226,0.07);
  border: 1px solid rgba(41,171,226,0.18);
  margin-bottom: 0.35rem;
  transition: background 0.18s;

  &:hover {
    background: rgba(41,171,226,0.15);
  }
`;

const SFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 0.5rem;
  padding-top: 0.5rem;
  border-top: 1px solid rgba(0, 0, 0, 0.05);
  margin-top: 0.1rem;
`;

const SFooterRight = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const SExpandedBlock = styled.div`
  padding: 0.75rem 0.9rem;
  margin: 0.2rem 0;
  border-radius: 12px;
  background: ${({ $color }) => `${$color}08`};
  border: 1px solid ${({ $color }) => `${$color}18`};
`;

const SExpandedLabel = styled.p`
  margin: 0 0 0.4rem;
  font-size: 0.6rem;
  font-weight: 900;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: ${({ $color }) => $color} !important;
  opacity: 1 !important;
`;

const SourceChip = styled.span`
  font-size: 0.68rem;
  font-weight: 700;
  color: var(--primary-dark);
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  background: rgba(41, 171, 226, 0.08);
  border: 1px solid rgba(41, 171, 226, 0.18);
  border-radius: 6px;
  padding: 0.16rem 0.48rem;

  &::before {
    content: '';
    width: 5px;
    height: 5px;
    border-radius: 50%;
    background: #29ABE2;
    flex-shrink: 0;
  }
`;

const ExpandBtn = styled.button`
  font-size: 0.68rem;
  font-weight: 800;
  color: ${({ $color }) => $color || 'var(--primary-color)'};
  cursor: pointer;
  padding: 0.24rem 0.7rem;
  border-radius: 8px;
  letter-spacing: 0.04em;
  background: ${({ $color }) => $color ? `${$color}10` : 'rgba(41,171,226,0.08)'};
  border: 1px solid ${({ $color }) => $color ? `${$color}25` : 'rgba(41,171,226,0.2)'};
  transition: all 0.18s;
  white-space: nowrap;

  &:hover {
    background: ${({ $color }) => $color ? `${$color}20` : 'rgba(41,171,226,0.16)'};
    opacity: 0.9;
  }
`;

// ─── Impact bars ──────────────────────────────────────────────────────────────

const BarsRow = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 2px;
`;

const Bar = styled.span`
  width: 4px;
  border-radius: 2px;
  background: ${({ $active, $color }) => ($active ? $color : 'rgba(0,0,0,0.1)')};

  &:nth-child(1) { height: 6px; }
  &:nth-child(2) { height: 10px; }
  &:nth-child(3) { height: 14px; }
`;

// ─── Sidebar widgets ──────────────────────────────────────────────────────────

const Widget = styled.div`
  background: #ffffff;
  border-radius: 20px;
  border: 1px solid var(--border-color);
  box-shadow: 0 4px 20px rgba(26, 82, 118, 0.08);
  overflow: hidden;
  transition: box-shadow 0.25s ease;

  &:hover {
    box-shadow: 0 8px 32px rgba(26, 82, 118, 0.12);
  }
`;

const WidgetHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 0.6rem;
  padding: 1rem 1.1rem;
  border-bottom: 1px solid var(--border-color);
  background: linear-gradient(135deg, var(--bg-secondary) 0%, #f0f8fd 100%);
`;

const WidgetIcon = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.82rem;
  color: ${({ $accent }) => ($accent ? '#F7941D' : 'var(--primary-dark)')};
  background: ${({ $accent }) => ($accent ? 'rgba(247,148,29,0.12)' : 'rgba(41,171,226,0.12)')};
  border: 1px solid ${({ $accent }) => ($accent ? 'rgba(247,148,29,0.22)' : 'rgba(41,171,226,0.2)')};
`;

const WidgetTitle = styled.h3`
  margin: 0;
  font-size: 0.72rem;
  font-weight: 900;
  color: var(--text-color);
  letter-spacing: 0.1em;
  text-transform: uppercase;
`;

const MacroList = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0;
`;

const MacroRow = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  padding: 0.9rem 1rem;
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
  border-right: 1px solid rgba(0, 0, 0, 0.05);
  transition: background 0.18s;
  gap: 0.3rem;

  &:nth-child(even) { border-right: none; }
  &:nth-last-child(-n+2) { border-bottom: none; }
  &:hover { background: var(--bg-secondary); }
`;

const MacroRowLeft = styled.div``;

const MacroLabel = styled.p`
  margin: 0;
  font-size: 0.75rem;
  font-weight: 700;
  color: var(--text-color);
`;

const MacroDate = styled.p`
  margin: 0.18rem 0 0;
  font-size: 0.62rem;
  color: #9ca3af;
`;

const MacroRight = styled.div`
  display: flex;
  align-items: center;
  gap: 0.4rem;
  margin-top: 0.15rem;
`;

const MacroVal = styled.span`
  font-size: 1rem;
  font-weight: 800;
  color: var(--text-color);
  font-variant-numeric: tabular-nums;
  letter-spacing: -0.01em;
`;

const TrendIcon = styled.span`
  width: 20px;
  height: 20px;
  border-radius: 6px;
  font-size: 0.65rem;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ $trend }) => ($trend === 'up' ? '#059669' : $trend === 'down' ? '#dc2626' : '#d97706')};
  background: ${({ $trend }) => ($trend === 'up' ? 'rgba(5,150,105,0.12)' : $trend === 'down' ? 'rgba(220,38,38,0.1)' : 'rgba(217,119,6,0.1)')};
  border: 1px solid ${({ $trend }) => ($trend === 'up' ? 'rgba(5,150,105,0.25)' : $trend === 'down' ? 'rgba(220,38,38,0.2)' : 'rgba(217,119,6,0.2)')};
`;

const InsightList = styled.div`
  display: flex;
  flex-direction: column;
`;

const InsightItem = styled.div`
  padding: 0.8rem 1.1rem;
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
  transition: background 0.18s;

  &:last-child { border-bottom: none; }
  &:hover { background: var(--bg-secondary); }
`;

const InsightKey = styled.p`
  margin: 0 0 0.28rem;
  font-size: 0.62rem;
  font-weight: 800;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--primary-color);
`;

const InsightVal = styled.p`
  margin: 0;
  font-size: 0.8rem;
  line-height: 1.55;
  color: var(--text-muted);
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

// ─── Skeleton ─────────────────────────────────────────────────────────────────

const SkeletonLine = styled.div`
  border-radius: 6px;
  background: linear-gradient(90deg, #e8f0f5 25%, #d4e4ee 50%, #e8f0f5 75%);
  background-size: 800px 100%;
  animation: ${skeletonBg} 1.4s ease-in-out infinite;
`;

const SkeletonRow = styled(SkeletonLine)`
  height: 12px;
  width: 80%;
  margin: 0.6rem 1rem;
  border-radius: 6px;
`;

const SkeletonFeatured = styled.div`
  height: 200px;
  border-radius: 20px;
  background: linear-gradient(90deg, #e8f0f5 25%, #d4e4ee 50%, #e8f0f5 75%);
  background-size: 800px 100%;
  animation: ${skeletonBg} 1.4s ease-in-out infinite;
`;

// ─── Error / empty states ─────────────────────────────────────────────────────

const ErrorBand = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 0.9rem 1.2rem;
  border-radius: 14px;
  background: rgba(239, 68, 68, 0.07);
  border: 1px solid rgba(239, 68, 68, 0.18);
  margin-bottom: 1rem;
`;

const ErrorMsg = styled.p`
  margin: 0;
  font-size: 0.88rem;
  color: #991b1b;
  font-weight: 600;
`;

const ErrorBtn = styled.button`
  padding: 0.42rem 0.9rem;
  border-radius: 10px;
  background: #dc2626;
  color: #fff;
  border: none;
  font-size: 0.8rem;
  font-weight: 700;
  cursor: pointer;
`;

const EmptyState = styled.div`
  padding: 3.5rem 2rem;
  text-align: center;
  color: var(--text-muted);
  font-size: 0.92rem;
  font-weight: 600;
  background: linear-gradient(135deg, #ffffff 0%, var(--bg-secondary) 100%);
  border-radius: 20px;
  border: 1.5px dashed var(--border-strong);
  box-shadow: 0 2px 12px rgba(26,82,118,0.05);
`;

// ─── Derivatives Pulse Widget styles ─────────────────────────────────────────

const IntelLoadWrap = styled.div`
  padding: 0.5rem 0;
`;

const IntelSection = styled.div`
  padding: 0.85rem 1.1rem;
  border-bottom: 1px solid rgba(0,0,0,0.05);

  &:last-of-type { border-bottom: none; }
`;

const IntelSectionLabel = styled.p`
  margin: 0 0 0.55rem;
  font-size: 0.6rem !important;
  font-weight: 900 !important;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: var(--text-muted) !important;
  opacity: 1 !important;
  display: flex;
  align-items: center;
  gap: 0.35rem;
`;

const IntelLabelNote = styled.span`
  font-weight: 500;
  letter-spacing: 0;
  text-transform: none;
  font-size: 0.58rem !important;
  color: #9ca3af !important;
`;

/* Funding Rates */
const FRRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.35rem 0.5rem;
  border-radius: 9px;
  margin-bottom: 0.25rem;
  background: var(--bg-secondary);
  border: 1px solid var(--border-color);
  cursor: default;
  transition: background 0.15s;

  &:hover { background: var(--bg-tertiary); }
  &:last-child { margin-bottom: 0; }
`;

const FRSymbol = styled.span`
  font-size: 0.7rem;
  font-weight: 900;
  color: var(--text-color);
  min-width: 34px;
  letter-spacing: 0.03em;
`;

const FRRate = styled.span`
  font-size: 0.7rem;
  font-weight: 800;
  font-variant-numeric: tabular-nums;
  color: ${({ $positive }) => ($positive ? '#059669' : '#dc2626')};
  min-width: 68px;
`;

const FRBias = styled.span`
  font-size: 0.58rem;
  font-weight: 700;
  padding: 0.18rem 0.44rem;
  border-radius: 999px;
  color: ${({ $color }) => $color};
  background: ${({ $color }) => `${$color}14`};
  border: 1px solid ${({ $color }) => `${$color}28`};
  margin-left: auto;
  white-space: nowrap;
`;

/* Long / Short Ratio */
const LSRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.4rem;

  &:last-child { margin-bottom: 0; }
`;

const LSSymbol = styled.span`
  font-size: 0.68rem;
  font-weight: 900;
  color: var(--text-color);
  min-width: 30px;
`;

const LSBarWrap = styled.div`
  flex: 1;
  height: 7px;
  border-radius: 999px;
  overflow: hidden;
  display: flex;
  background: rgba(0,0,0,0.06);
`;

const LSBarLong = styled.div`
  height: 100%;
  background: linear-gradient(90deg, #059669, #34d399);
  border-radius: 999px 0 0 999px;
  transition: width 0.7s cubic-bezier(0.22,1,0.36,1);
`;

const LSBarShort = styled.div`
  height: 100%;
  background: linear-gradient(90deg, #f87171, #dc2626);
  border-radius: 0 999px 999px 0;
  transition: width 0.7s cubic-bezier(0.22,1,0.36,1);
`;

const LSPcts = styled.div`
  display: flex;
  gap: 0.3rem;
  min-width: 72px;
  justify-content: flex-end;
`;

const LSPct = styled.span`
  font-size: 0.62rem;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  color: ${({ $up }) => ($up ? '#059669' : '#dc2626')};
`;

/* Open Interest */
const OIGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 0.45rem;
`;

const OICell = styled.div`
  background: var(--bg-secondary);
  border: 1px solid var(--border-color);
  border-radius: 11px;
  padding: 0.55rem 0.65rem;
`;

const OISymbol = styled.p`
  margin: 0 0 0.15rem;
  font-size: 0.6rem !important;
  font-weight: 900 !important;
  letter-spacing: 0.08em;
  color: var(--text-muted) !important;
  opacity: 1 !important;
`;

const OIVal = styled.p`
  margin: 0;
  font-size: 0.86rem !important;
  font-weight: 800 !important;
  color: var(--text-color) !important;
  font-variant-numeric: tabular-nums;
  opacity: 1 !important;
`;

const OIChange = styled.span`
  display: inline-block;
  margin-top: 0.18rem;
  font-size: 0.6rem;
  font-weight: 700;
  color: ${({ $up }) => ($up ? '#059669' : '#dc2626')};
`;

/* CTA */
const IntelCTA = styled.a`
  display: flex;
  align-items: center;
  gap: 0.7rem;
  padding: 0.9rem 1.1rem;
  background: linear-gradient(135deg, #071d2e 0%, #0a2d48 100%);
  text-decoration: none;
  transition: opacity 0.2s;

  &:hover { opacity: 0.9; }
`;

const IntelCTAIcon = styled.div`
  width: 34px;
  height: 34px;
  border-radius: 10px;
  background: rgba(247,148,29,0.18);
  border: 1px solid rgba(247,148,29,0.3);
  color: #F7941D;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.82rem;
  flex-shrink: 0;
`;

const IntelCTAText = styled.div`
  flex: 1;
`;

const IntelCTATitle = styled.p`
  margin: 0 0 0.18rem;
  font-size: 0.74rem !important;
  font-weight: 800 !important;
  color: #ffffff !important;
  opacity: 1 !important;
`;

const IntelCTADesc = styled.p`
  margin: 0;
  font-size: 0.62rem !important;
  color: rgba(180,210,235,0.75) !important;
  font-weight: 500 !important;
  opacity: 1 !important;
`;

const IntelCTAArrow = styled.span`
  font-size: 1rem;
  color: #F7941D;
  font-weight: 700;
`;

const DerivLiveTag = styled.div`
  display: flex;
  align-items: center;
  gap: 0.3rem;
  margin-left: auto;
  font-size: 0.58rem;
  font-weight: 700;
  color: #6b7280;
  letter-spacing: 0.03em;
  white-space: nowrap;
`;

const DerivLiveDot = styled.span`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #10b981;
  flex-shrink: 0;
  animation: ${pulseDot} 1.6s ease-in-out infinite;
`;

/* ─── Fibonacci Retracement Styled Components ──────────────────────────────── */

const FiboEducNote = styled.div`
  display: flex;
  gap: 0.4rem;
  align-items: flex-start;
  background: #e8f6fd;
  border: 1px solid var(--border-color);
  border-left: 3px solid var(--primary-color);
  border-radius: 6px;
  padding: 0.45rem 0.55rem;
  margin-bottom: 0.7rem;
`;

const FiboEducIcon = styled.span`
  font-size: 0.72rem;
  flex-shrink: 0;
  margin-top: 0.05rem;
`;

const FiboEducText = styled.p`
  margin: 0;
  font-size: 0.61rem !important;
  color: var(--text-muted) !important;
  line-height: 1.55;
  opacity: 1 !important;
  strong { color: var(--text-color) !important; font-weight: 700; }
  em { font-style: normal; color: var(--primary-dark) !important; font-weight: 600; }
`;

const FiboBlock = styled.div`
  background: var(--bg-secondary);
  border: 1px solid var(--border-color);
  border-radius: 10px;
  padding: 0.65rem 0.7rem 0.6rem;
  margin-bottom: 0.6rem;
  &:last-child { margin-bottom: 0; }
`;

const FiboBlockHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 0.4rem;
  margin-bottom: 0.55rem;
  flex-wrap: wrap;
`;

const FiboBlockSymbol = styled.span`
  font-size: 0.78rem !important;
  font-weight: 900 !important;
  color: var(--navy-color) !important;
  letter-spacing: 0.06em;
`;

const FiboBlockPrice = styled.span`
  font-size: 0.7rem !important;
  font-weight: 700 !important;
  color: var(--accent-color) !important;
`;

const FiboNearestBadge = styled.span`
  margin-left: auto;
  font-size: 0.57rem !important;
  font-weight: 700 !important;
  color: ${({ $support }) => $support ? '#059669' : '#dc2626'} !important;
  background: ${({ $support }) => $support ? 'rgba(5,150,105,0.1)' : 'rgba(220,38,38,0.08)'};
  border: 1px solid ${({ $support }) => $support ? 'rgba(5,150,105,0.25)' : 'rgba(220,38,38,0.22)'};
  border-radius: 20px;
  padding: 0.12rem 0.5rem;
  white-space: nowrap;
`;

/* Visual price bar */
const FiboTrackWrap = styled.div`
  display: flex;
  align-items: center;
  gap: 0.35rem;
  margin-bottom: 0.2rem;
`;

const FiboTrackEndLabel = styled.span`
  font-size: 0.54rem !important;
  font-weight: 700 !important;
  color: var(--text-body-soft) !important;
  flex-shrink: 0;
  width: 0.7rem;
  text-align: center;
`;

const FiboTrackBarOuter = styled.div`
  flex: 1;
  position: relative;
  height: 20px;
`;

const FiboTrackLine = styled.div`
  position: absolute;
  top: 50%;
  left: 0;
  right: 0;
  height: 3px;
  background: var(--border-strong);
  border-radius: 2px;
  transform: translateY(-50%);
`;

const FiboLevelTick = styled.div`
  position: absolute;
  top: 50%;
  left: ${({ $pct }) => $pct}%;
  transform: translate(-50%, -50%);
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${({ $color }) => $color};
  border: 2px solid #ffffff;
  box-shadow: 0 0 0 1px ${({ $color }) => $color}55;
  z-index: 1;
`;

const FiboPriceMarker = styled.div`
  position: absolute;
  top: 0;
  bottom: 0;
  left: ${({ $pct }) => $pct}%;
  transform: translateX(-50%);
  width: 2.5px;
  background: var(--navy-color);
  border-radius: 2px;
  z-index: 2;
  &::after {
    content: '▼';
    position: absolute;
    top: -1px;
    left: 50%;
    transform: translateX(-50%);
    font-size: 0.42rem;
    color: var(--navy-color);
    line-height: 1;
  }
`;

const FiboTrackCaption = styled.div`
  font-size: 0.55rem !important;
  color: var(--text-body-soft) !important;
  text-align: center;
  margin-bottom: 0.5rem;
  letter-spacing: 0.01em;
`;

/* Group labels */
const FiboGroupLabel = styled.div`
  font-size: 0.58rem !important;
  font-weight: 800 !important;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: ${({ $resist }) => $resist ? '#dc2626' : '#059669'} !important;
  background: ${({ $resist }) => $resist ? 'rgba(220,38,38,0.06)' : 'rgba(5,150,105,0.07)'};
  border: 1px solid ${({ $resist }) => $resist ? 'rgba(220,38,38,0.18)' : 'rgba(5,150,105,0.2)'};
  border-radius: 4px;
  padding: 0.18rem 0.45rem;
  margin: 0.4rem 0 0.2rem;
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  &:first-of-type { margin-top: 0; }
`;

/* Level rows */
const FiboRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.4rem;
  padding: 0.3rem 0.35rem;
  border-radius: 6px;
  margin-bottom: 0.2rem;
  background: ${({ $highlight }) => $highlight ? 'rgba(247,148,29,0.07)' : $anchor => $anchor ? 'rgba(148,163,184,0.05)' : '#ffffff'};
  border: ${({ $highlight, $anchor }) =>
    $highlight ? '1px solid rgba(247,148,29,0.3)' :
    $anchor    ? '1px dashed var(--border-color)' :
                 '1px solid var(--border-color)'};
  transition: background 0.2s;
  &:last-child { margin-bottom: 0; }
`;

const FiboRatioTag = styled.span`
  font-size: 0.59rem !important;
  font-weight: 800 !important;
  color: ${({ $color }) => $color} !important;
  background: ${({ $color }) => `${$color}14`};
  border: 1px solid ${({ $color }) => `${$color}35`};
  border-radius: 4px;
  padding: 0.1rem 0.3rem;
  min-width: 2.8rem;
  text-align: center;
  white-space: nowrap;
  flex-shrink: 0;
  margin-top: 0.06rem;
`;

const FiboLevelInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.07rem;
  flex: 1;
  min-width: 0;
`;

const FiboLevelPrice = styled.span`
  font-size: 0.67rem !important;
  font-weight: 700 !important;
  color: var(--text-color) !important;
  line-height: 1.2;
`;

const FiboLevelDesc = styled.span`
  font-size: 0.55rem !important;
  font-weight: 500 !important;
  color: var(--text-body-soft) !important;
  line-height: 1.2;
`;

const FiboStatusTag = styled.span`
  font-size: 0.58rem !important;
  font-weight: 700 !important;
  color: ${({ $up }) => $up ? '#059669' : '#dc2626'} !important;
  white-space: nowrap;
  flex-shrink: 0;
  margin-top: 0.1rem;
`;
